import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, functions } from '../firebase';
import { signInWithEmailAndPassword } from "firebase/auth";
import { httpsCallable } from "firebase/functions";

// MUI Bileşenleri
import {
    Box,
    TextField,
    Button,
    Typography,
    Alert,
    CircularProgress,
    Paper
} from '@mui/material';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import PublicShell from '../components/PublicShell';

// Alan etiketi: mockup'taki gibi alanın üstünde, koyu ve kalın
function FieldLabel({ htmlFor, children }) {
    return (
        <Typography
            component="label"
            htmlFor={htmlFor}
            sx={{ display: 'block', mb: 0.75, fontSize: 14, fontWeight: 600, color: '#34415A' }}
        >
            {children}
        </Typography>
    );
}

function LoginPage() {
    // Sizin kodunuzdaki state'leri kullanıyoruz
    const [usernameOrEmail, setUsernameOrEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    // Sizin handleLogin mantığınız (kullaniciAdi arama özellikli)
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            let emailToUse = usernameOrEmail.trim(); // Boşlukları temizle

            // Eğer @ işareti yoksa, kullanıcı adı olarak kabul et ve e-postayı bul
            if (!emailToUse.includes('@')) {
                // users koleksiyonu herkese açık değil; e-posta Cloud Function ile bulunur
                try {
                    const getLoginEmail = httpsCallable(functions, 'getLoginEmail');
                    const { data } = await getLoginEmail({ kullaniciAdi: emailToUse });
                    emailToUse = data.email;
                } catch (lookupError) {
                    if (lookupError.code === 'functions/not-found') {
                        setError('Kullanıcı adı bulunamadı.');
                        return;
                    }
                    throw lookupError;
                }
            }

            // E-posta ile giriş yap
            await signInWithEmailAndPassword(auth, emailToUse, password);
            navigate('/home'); // Başarılı girişte yönlendir

        } catch (err) {
            console.error(err);
            if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
                setError('Geçersiz kullanıcı adı / e-posta veya şifre.');
            } else {
                setError('Giriş yapılamadı. Lütfen tekrar deneyin.');
            }
        } finally {
            setLoading(false);
        }
    };

    // Sizin sertifika doğrulama fonksiyonunuz
    const handleVerifyCertificate = () => {
        navigate('/verify-certificate');
    };

    return (
        <PublicShell title="Giriş" footer="Hesabınız yoksa eğitim yöneticinize başvurun.">
            <Paper
                component="form"
                onSubmit={handleSubmit}
                noValidate
                sx={{
                    width: '100%',
                    maxWidth: 440,
                    p: { xs: 3, sm: 4.5 },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3
                }}
            >
                <Box>
                    <Typography component="h1" variant="h5" sx={{ color: 'primary.main', fontSize: 28 }}>
                        Giriş yap
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: 15 }}>
                        Eğitim ve sertifikasyon portalı
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error">
                        {error}
                    </Alert>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                        <FieldLabel htmlFor="usernameOrEmail">Kullanıcı adı veya e-posta</FieldLabel>
                        <TextField
                            required
                            fullWidth
                            hiddenLabel
                            id="usernameOrEmail"
                            name="usernameOrEmail"
                            placeholder="ad.soyad@arca.aero"
                            autoComplete="email"
                            autoFocus
                            value={usernameOrEmail}
                            onChange={(e) => setUsernameOrEmail(e.target.value)}
                            disabled={loading}
                        />
                    </Box>
                    <Box>
                        <FieldLabel htmlFor="password">Şifre</FieldLabel>
                        <TextField
                            required
                            fullWidth
                            hiddenLabel
                            name="password"
                            type="password"
                            id="password"
                            placeholder="••••••••"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={loading}
                        />
                    </Box>
                </Box>

                <Box sx={{ position: 'relative' }}>
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={loading}
                        size="large"
                        sx={{ height: 48, fontSize: 16 }}
                    >
                        {loading ? 'Giriş yapılıyor...' : 'Giriş yap'}
                    </Button>
                    {loading && (
                        <CircularProgress
                            size={24}
                            sx={{
                                color: 'primary.main',
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                marginTop: '-12px',
                                marginLeft: '-12px',
                            }}
                        />
                    )}
                </Box>

                {/* Sertifika doğrulama: kırmızı çerçeveli ikincil buton */}
                <Button
                    type="button"
                    fullWidth
                    variant="outlined"
                    onClick={handleVerifyCertificate}
                    disabled={loading}
                    startIcon={<VerifiedUserOutlinedIcon />}
                    sx={{
                        height: 44,
                        fontSize: 15,
                        color: 'error.main',
                        borderColor: 'brand.red',
                        '&:hover': { borderColor: 'error.dark', bgcolor: '#FEE6E6' }
                    }}
                >
                    Sertifika doğrula
                </Button>
            </Paper>
        </PublicShell>
    );
}

export default LoginPage;
